import { IconCheck, IconThumbDown, IconThumbUp } from "@tabler/icons-react"
import { useId, useState } from "react"

import type { AiFeedbackGenerationType } from "@forento/core/models/ai"
import { cn } from "@forento/ui/utilities/tailwind"

import { query } from "~/utilities/trpc"

type Props = { id: number; type: AiFeedbackGenerationType }

export default function AiGenerationFeedback(props: Props) {
	// Reset draft comment state when another generation replaces the result in the same editor.
	return <Feedback key={`${props.type}-${props.id}`} {...props} />
}

function Feedback({ id, type }: Props) {
	const input = { id, type }
	const feedback = query.ai.generationFeedbackState.useQuery(input)
	const utils = query.useUtils()
	const commentId = useId()
	const [comment, setComment] = useState<string | null>(null)
	const [saved, setSaved] = useState(false)
	const mutation = query.ai.generationFeedback.useMutation({
		onSuccess: (_data, variables) => {
			utils.ai.generationFeedbackState.setData(input, {
				positive: variables.positive,
				comment: variables.comment?.trim() || null,
			})
			setComment(null)
			setSaved(true)
		},
	})
	const positive = mutation.isPending ? mutation.variables.positive : (feedback.data?.positive ?? null)
	const commentValue = comment ?? feedback.data?.comment ?? ""
	const hasCommentChanges = commentValue.trim() !== (feedback.data?.comment ?? "")
	const disabled = feedback.data === undefined || mutation.isPending
	const save = (rating: boolean | null) => {
		setSaved(false)
		mutation.mutate({ id, type, positive: rating, comment: rating === null ? null : commentValue || null })
	}

	return (
		<div className="border-primary/20 bg-primary/5 w-full min-w-0 rounded-xl border p-4 text-left">
			<p className="text-sm font-semibold text-gray-900">How did Forento AI do?</p>
			<p className="mt-1 text-xs leading-5 text-gray-600">Your feedback helps us improve future generations.</p>
			<div className="mt-3 grid grid-cols-2 gap-2">
				{[true, false].map(value => (
					<button
						key={String(value)}
						type="button"
						aria-label={value ? "Thumbs up" : "Thumbs down"}
						aria-pressed={positive === value}
						title={positive === value ? "Click to remove your rating" : undefined}
						disabled={disabled}
						className={cn(
							"focus-visible:outline-primary flex min-h-11 items-center justify-center gap-2 rounded-lg border px-3 py-2.5 text-sm font-semibold transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 disabled:cursor-wait",
							positive === value
								? "border-primary bg-primary text-white shadow-sm"
								: "hover:border-primary/50 hover:bg-primary/5 border-gray-300 bg-white text-gray-700",
						)}
						onClick={() => save(positive === value ? null : value)}
					>
						{value ? (
							<IconThumbUp className="size-5 shrink-0" />
						) : (
							<IconThumbDown className="size-5 shrink-0" />
						)}
						{value ? "Helpful" : "Needs work"}
					</button>
				))}
			</div>
			{positive !== null && (
				<div className="mt-4 flex flex-col gap-2">
					<label htmlFor={commentId} className="text-sm font-medium text-gray-800">
						{positive ? "What worked well?" : "What could be better?"}
						<span className="ml-1 text-xs font-normal text-gray-500">(optional)</span>
					</label>
					<textarea
						id={commentId}
						placeholder={
							positive
								? "Tell us what you liked about this result…"
								: "Tell us what was missing or needs improving…"
						}
						maxLength={2000}
						rows={3}
						value={commentValue}
						disabled={disabled}
						className="focus:border-primary focus:ring-primary/15 min-h-24 w-full resize-y rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm leading-6 text-gray-900 placeholder:text-gray-400 focus:ring-2 focus:outline-none"
						onChange={event => {
							setComment(event.target.value)
							setSaved(false)
						}}
					/>
					<div className="mt-1 flex flex-wrap items-center justify-between gap-2">
						<span role="status" className="flex items-center gap-1.5 text-xs text-gray-600">
							{mutation.isPending ? (
								"Saving…"
							) : (
								<>
									<IconCheck className="text-primary size-4" />
									{saved && !hasCommentChanges ? "Feedback saved" : "Rating saved"}
								</>
							)}
						</span>
						<button
							type="button"
							disabled={disabled || !hasCommentChanges}
							className="bg-primary hover:bg-primary/90 focus-visible:outline-primary rounded-lg px-3 py-2 text-sm font-semibold text-white transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 disabled:cursor-default disabled:bg-gray-200 disabled:text-gray-500"
							onClick={() => save(positive)}
						>
							Save comment
						</button>
					</div>
				</div>
			)}
			{positive === null && mutation.isPending && (
				<p role="status" className="mt-3 text-xs text-gray-600">
					Saving…
				</p>
			)}
			{positive === null && saved && !mutation.isPending && (
				<p role="status" className="mt-3 text-xs text-gray-600">
					Rating removed.
				</p>
			)}
			{feedback.isError && (
				<p role="alert" className="mt-3 text-sm text-red-700">
					Could not load feedback.{" "}
					<button type="button" className="underline" onClick={() => feedback.refetch()}>
						Try again
					</button>
				</p>
			)}
			{mutation.isError && (
				<p role="alert" className="mt-3 text-sm text-red-700">
					Could not save feedback. Please try again.
				</p>
			)}
		</div>
	)
}
